import React from "react";
import { Link } from "react-router-dom";
import { dashboardRepo, invoiceRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Card, EmptyState, PageHead, Skeleton, StatCard, StatusBadge, baht, compactMoney, formatTime, useAsync } from "../../components/ui";
import { BarChart, DoughnutChart, LineChart } from "../../components/ui/charts";
import Icon from "../../components/ui/Icon";
import ShopLinkCard from "../../components/ShopLinkCard";

const CHANNEL_LABEL = { line: "LINE", facebook: "Facebook", instagram: "Instagram", walkin: "Walk-in", phone: "โทรศัพท์", website: "เว็บไซต์" };

export default function Dashboard() {
  const { orgId, branchId, organization, noOrg, businessModel, hasModule } = useApp();
  const { tx } = useI18n();
  const { data, loading } = useAsync(() => dashboardRepo.summary(orgId, branchId), [orgId, branchId]);
  // จำนวนใบแจ้งหนี้จริงทั้งหมด (recentInvoices ถูกตัดไว้แค่ 6 ใบ ใช้นับไม่ได้)
  const { data: invoiceTotal } = useAsync(() => invoiceRepo.all(orgId, (row) => !branchId || row.branchId === branchId).then((rows) => rows.length), [orgId, branchId], 0);

  if (loading || !data) {
    return (
      <>
        <PageHead title={tx("แดชบอร์ดภาพรวม", "Overview dashboard")} />
        <div className="stat-grid">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} height={104} />
          ))}
        </div>
        <Skeleton height={300} />
      </>
    );
  }

  // แนวโน้มรายได้: เทียบเดือนนี้กับเดือนก่อน (ใบที่ชำระแล้ว) — ไม่มีฐานเทียบ/ไม่เปลี่ยนแปลง = ไม่แสดง
  const months = data.revenueByMonth || [];
  const curMonth = months[months.length - 1]?.amount || 0;
  const prevMonth = months[months.length - 2]?.amount || 0;
  const revenueChange = prevMonth > 0 ? ((curMonth - prevMonth) / prevMonth) * 100 : null;
  const revenueTrend = revenueChange && Math.abs(revenueChange) >= 0.05
    ? { dir: revenueChange > 0 ? "up" : "down", text: `${Math.abs(revenueChange).toFixed(1)}% ${tx("จากเดือนก่อน", "vs last month")}` }
    : undefined;

  const channelLabels = Object.keys(data.channelCount).map((key) => CHANNEL_LABEL[key] || key);
  const channelValues = Object.values(data.channelCount);

  return (
    <>
      <PageHead
        title={tx("แดชบอร์ดภาพรวม", "Overview dashboard")}
        subtitle={`${organization?.name || (noOrg ? tx("ยังไม่ได้ผูกกับร้าน — ติดต่อทีมงาน", "Not linked to a shop — contact support") : "")}${organization?.name || noOrg ? " · " : ""}${tx("ข้อมูลสรุปธุรกิจแบบเรียลไทม์", "Real-time business summary")}`}
      />

      <div className="stat-grid">
        <StatCard label={tx("รายได้เดือนนี้", "Revenue this month")} icon={<Icon name="money" />} value={compactMoney(data.revenueThisMonth)} hint={tx(`รายได้สะสม ${compactMoney(data.revenueTotal)}`, `Total ${compactMoney(data.revenueTotal)}`)} trend={revenueTrend} />
        <StatCard label={tx("ยอดค้างชำระ", "Outstanding")} icon={<Icon name="receipt" />} value={compactMoney(data.outstanding)} hint={tx(`เกินกำหนด ${data.overdueCount} ใบ`, `${data.overdueCount} overdue`)} tone="var(--c-rose)" />
        <StatCard label={tx("ลูกค้าทั้งหมด", "Customers")} icon={<Icon name="users" />} value={data.customerTotal} hint={tx(`ใหม่เดือนนี้ ${data.newCustomerThisMonth} ราย`, `${data.newCustomerThisMonth} new this month`)} />
        {hasModule("booking") ? (
          <StatCard label={tx("นัดหมายวันนี้", "Appointments today")} icon={<Icon name="calendar" />} value={data.appointmentToday} hint={tx(`รอถึงคิว ${data.appointmentUpcoming} รายการ`, `${data.appointmentUpcoming} upcoming`)} />
        ) : null}
        <StatCard label={tx("ลีดที่ยังเปิดอยู่", "Open leads")} icon={<Icon name="target" />} value={data.leadOpen} hint={tx(`มูลค่ารวม ${compactMoney(data.leadValue)}`, `Value ${compactMoney(data.leadValue)}`)} />
        {hasModule("pos") ? <StatCard label={tx("ยอดขายหน้าร้านวันนี้", "POS today")} icon={<Icon name="receipt" />} value={compactMoney(data.posToday)} /> : null}
        {hasModule("slip") ? <StatCard label={tx("สลิปรอตรวจสอบ", "Slips pending")} icon={<Icon name="checkSquare" />} value={data.slipPending} tone={data.slipPending ? "var(--c-amber)" : undefined} /> : null}
        {hasModule("installment") ? <StatCard label={tx("ค่างวดเกินกำหนด", "Overdue installments")} icon={<Icon name="chartLine" />} value={data.installmentOverdue} tone={data.installmentOverdue ? "var(--c-rose)" : undefined} /> : null}
        {hasModule("inventory") ? <StatCard label={tx("สินค้าใกล้หมด", "Low stock")} icon={<Icon name="box" />} value={data.lowStock} /> : null}
        {hasModule("oms") ? <StatCard label={tx("ออเดอร์รอจัดส่ง", "Orders to ship")} icon={<Icon name="cart" />} value={data.orderPending} /> : null}
      </div>

      <div className="grid-sidebar--right grid-sidebar">
        <Card title={tx("รายได้ย้อนหลัง 12 เดือน", "Revenue last 12 months")} subtitle={tx("นับเฉพาะใบแจ้งหนี้ที่ชำระแล้ว", "Paid invoices only")}>
          <LineChart labels={data.revenueByMonth.map((row) => row.label.slice(2))} data={data.revenueByMonth.map((row) => row.amount)} height={260} />
        </Card>

        <Card title={tx("ช่องทางที่ลูกค้าเข้ามา", "Customer channels")}>
          <DoughnutChart labels={channelLabels} data={channelValues} height={260} />
        </Card>
      </div>

      <div className="grid-2">
        {hasModule("booking") ? (
          <Card
            title={tx("คิววันนี้", "Today's schedule")}
            actions={<Link to="/admin/calendar" className="btn-crm btn-sm-crm btn-soft-crm">{tx("ดูปฏิทิน", "Calendar")}</Link>}
          >
            {data.todayAppointments.length === 0 ? (
              <EmptyState icon={<Icon name="calendarAlt" />} text={tx("วันนี้ยังไม่มีคิว", "No appointments today")} />
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {data.todayAppointments.map((item) => (
                  <div key={item.id} className="u-between" style={{ borderBottom: "1px solid var(--c-slate-100)", paddingBottom: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="u-strong">{item.customerName}</div>
                      <div className="u-fs-12 u-muted">{item.serviceName}</div>
                    </div>
                    <div className="u-right">
                      <div className="u-strong">{formatTime(item.startAt)}</div>
                      <StatusBadge status={item.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ) : null}

        <Card
          title={tx("ใบแจ้งหนี้ล่าสุด", "Recent invoices")}
          actions={<Link to="/admin/billing" className="btn-crm btn-sm-crm btn-soft-crm">{tx("ดูทั้งหมด", "View all")}</Link>}
        >
          <div style={{ display: "grid", gap: 10 }}>
            {data.recentInvoices.map((invoice) => (
              <div key={invoice.id} className="u-between" style={{ borderBottom: "1px solid var(--c-slate-100)", paddingBottom: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="u-strong">{invoice.docNo}</div>
                  <div className="u-fs-12 u-muted">{invoice.customerName}</div>
                </div>
                <div className="u-right">
                  <div className="u-strong">{baht(invoice.total)}</div>
                  <StatusBadge status={invoice.status} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title={tx("โมดูลที่เปิดใช้งาน & ภาพรวมจำนวน", "Enabled modules & counts")} subtitle={tx("ปรับได้ที่หน้าจัดการโมดูล", "Adjust in Module Manager")}>
        <div className="u-flex u-wrap u-gap-8 u-mb-16">
          {businessModel.modules.map((key) => (
            <Badge key={key} tone="indigo">{key}</Badge>
          ))}
        </div>
        <BarChart
          labels={[tx("ลูกค้า", "Customers"), tx("นัดหมายที่จะถึง", "Upcoming bookings"), tx("ออเดอร์รอจัดส่ง", "Orders to ship"), tx("ใบแจ้งหนี้ทั้งหมด", "All invoices"), tx("ลีดที่เปิดอยู่", "Open leads")]}
          data={[data.customerTotal, data.appointmentUpcoming, data.orderPending, invoiceTotal || 0, data.leadOpen]}
          height={220}
        />
      </Card>

      <ShopLinkCard />
    </>
  );
}
